/**
 * Which action types a person can supply by typing.
 *
 * A deterministic check knows a meta description is missing without knowing
 * what it should say, so most recommendations arrive with no payload. Where
 * the missing piece is a single piece of text, a person can write it. Where it
 * is a link target, an image, or JSON-LD, a text box would only move the
 * failure later, so those are named as manual rather than offered.
 *
 * The rules mirror the server's payload validators so a mistake is caught
 * while the text is still on screen rather than after a round trip.
 */

export interface FieldSpec {
  label: string;
  placeholder: string;
  multiline: boolean;
  minLength: number;
  maxLength: number;
  /** Extra rules the server enforces, checked here for a faster answer. */
  validate?: (value: string) => string | null;
}

const noMarkup = (value: string): string | null =>
  value.includes('<') || value.includes('>') ? 'cannot contain HTML markup' : null;

export const WRITABLE_ACTIONS: Record<string, FieldSpec> = {
  UPDATE_TITLE: {
    label: 'New title',
    placeholder: 'Cloud accounting for small businesses — Acme',
    multiline: false,
    minLength: 3,
    maxLength: 300,
    validate: (value) => (/[\r\n]/.test(value) ? 'cannot contain line breaks' : noMarkup(value)),
  },
  UPDATE_META_DESCRIPTION: {
    label: 'New meta description',
    placeholder: 'One or two sentences stating the benefit and giving a reason to click.',
    multiline: true,
    minLength: 10,
    maxLength: 500,
    validate: noMarkup,
  },
  UPDATE_HEADING: {
    label: 'New heading',
    placeholder: 'What this page is about',
    multiline: false,
    minLength: 3,
    maxLength: 300,
    validate: noMarkup,
  },
};

export function isWritable(actionType: string | null | undefined): boolean {
  return Boolean(actionType && actionType in WRITABLE_ACTIONS);
}

/**
 * Which types the metadata agent can write for.
 *
 * Narrower than the writable set: headings are typeable but the agent is not
 * prompted for them, and offering "write with AI" for something it will not
 * write would repeat the original mistake of a button that cannot deliver.
 * Mirrors DRAFTABLE_ACTION_TYPES on the server.
 */
const DRAFTABLE = new Set(['UPDATE_TITLE', 'UPDATE_META_DESCRIPTION']);

export function isDraftable(actionType: string | null | undefined): boolean {
  return Boolean(actionType && DRAFTABLE.has(actionType));
}

/** Validates one value against its action type's rules. Null means valid. */
export function validateValue(value: string, spec: FieldSpec): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'required';
  if (trimmed.length < spec.minLength) return `at least ${spec.minLength} characters`;
  if (trimmed.length > spec.maxLength) return `at most ${spec.maxLength} characters`;
  return spec.validate?.(trimmed) ?? null;
}
